import axios from 'axios';
import { TokenManager } from './api';
import { API_CONFIG } from '../config/apiConfig';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

interface UpdateProfileRequest {
  province: string;
  ward: string;
  gender: 'male' | 'female' | 'other';
  birthYear: number;
}

interface User {
  id: string;
  name: string;
  phone: string;
  address?: string;
  province?: string;
  ward?: string;
  gender?: 'male' | 'female' | 'other';
  birthYear?: number;
  email?: string;
  energy: number;
  isAppUser: boolean;
  createdAt: string;
  lastLoginAt?: string;
  firebaseUid?: string;
  phoneVerified: boolean;
  verifiedPhoneNumber?: string;
}

class UserApiService {
  private getAuthHeaders(): { [key: string]: string } {
    // Get token from TokenManager
    const token = TokenManager.getAccessToken();
    
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
    };
  }

  /**
   * Refresh token and retry the request
   */
  private async refreshTokenAndRetry<T>(requestFn: () => Promise<T>): Promise<T> {
    const refreshToken = TokenManager.getRefreshToken();
    
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const response = await axios.post(`${API_CONFIG.BASE_URL}/auth/refresh-token`, {
        refreshToken
      });

      const { accessToken, refreshToken: newRefreshToken } = response.data.data;
      TokenManager.setTokens(accessToken, newRefreshToken);

      // Retry the original request with new token
      return await requestFn();
    } catch (refreshError) {
      // If refresh fails, clear tokens and throw error
      TokenManager.clearTokens();
      throw new Error('Token refresh failed');
    }
  }

  /**
   * Handle API request with automatic token refresh on 401/403 errors
   */
  private async makeRequest<T>(requestFn: () => Promise<T>): Promise<T> {
    try {
      return await requestFn();
    } catch (error: any) {
      // Handle 401 (Unauthorized) and 403 (Forbidden) errors
      if (error.response?.status === 401 || error.response?.status === 403) {
        try {
          return await this.refreshTokenAndRetry(requestFn);
        } catch (refreshError) {
          throw error; // Throw original error if refresh fails
        }
      }
      throw error;
    }
  }

  /**
   * Get current user profile
   */
  async getProfile(): Promise<ApiResponse<User>> {
    try {
      const response = await this.makeRequest(async () => {
        const headers = this.getAuthHeaders();
        return await axios.get(
          `${API_CONFIG.BASE_URL}/users/profile`,
          { headers }
        );
      });

      return response.data;
    } catch (error: any) {
      console.error('Get profile error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get profile'
      };
    }
  }

  /**
   * Update user profile
   */
  async updateProfile(profileData: UpdateProfileRequest): Promise<ApiResponse<User>> {
    try {
      const response = await this.makeRequest(async () => {
        const headers = this.getAuthHeaders();
        return await axios.put(
          `${API_CONFIG.BASE_URL}/users/profile`,
          profileData,
          { headers }
        );
      });

      return response.data;
    } catch (error: any) {
      console.error('Update profile error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update profile'
      };
    }
  }

  /**
   * Submit referral code
   */
  async submitReferralCode(referrerPhone: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.makeRequest(async () => {
        const headers = this.getAuthHeaders();
        return await axios.post(
          `${API_CONFIG.BASE_URL}/users/referral-code`,
          { referrerPhone: referrerPhone.trim() },
          { headers }
        );
      });

      return response.data;
    } catch (error: any) {
      console.error('Submit referral code error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to submit referral code',
        message: error.response?.data?.message || 'Có lỗi xảy ra khi nhập mã mời'
      };
    }
  }
}

export const userApiService = new UserApiService();
export type { UpdateProfileRequest, User };
